import {ButtonGroup, Checkbox} from '@kit';

import React from 'react';

import {DamageType, SourceBook, SpellFilterInput, SpellSchool} from '../../../generated/types';
import {
	enumList,
	Filters,
	FiltersPopup,
	FilterValues,
	flag,
	intList,
	nullableEnumList,
} from '../../../components/filters';
import {recordToString} from '../../../components/render-helpers';
import {damageTypesToString, sourceBookToString, spellSchoolToString} from '../../../components/enums';

import formCss from '../../../components/form.module.css';

const components: Record<string, string> = {m: 'Материальный', s: 'Жестовый', v: 'Словесный'};

const castingTimes: Record<string, string> = {
	action: 'Действие',
	bonus: 'Бонусное действие',
	reaction: 'Ответное действие',
	'1m': '1 минута',
	'10m': '10 минут',
	'1h': '1 час',
	'8h': '8 часов',
	'12h': '12 часов',
	'24h': '24 часа',
};

const classes: Record<string, string> = {
	bard: 'Бард',
	wizard: 'Волшебник',
	druid: 'Друид',
	cleric: 'Жрец',
	warlock: 'Колдун',
	paladin: 'Паладин',
	ranger: 'Следопыт',
	sorcerer: 'Чародей',
};

const LEVELS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const SOURCES = [SourceBook.XPHB, SourceBook.AU, SourceBook.FRHoF, SourceBook.DDBD];

export const spellFilters = {
	classes: enumList('class', Object.keys(classes)),
	levels: intList('level', LEVELS),
	ritual: flag('ritual'),
	concentration: flag('concentration'),
	schools: enumList('school', Object.values(SpellSchool)),
	components: enumList('component', Object.keys(components)),
	times: enumList('time', Object.keys(castingTimes)),
	damageTypes: nullableEnumList('damage', Object.values(DamageType)),
	sources: enumList('source', SOURCES),
};

export const toSpellFilterInput = ({damageTypes, ...values}: FilterValues<typeof spellFilters>): SpellFilterInput => ({
	...values,
	// null в списке — «без урона»; в сгенерированных типах null не выразить, а undefined в массиве JSON тоже отдаёт как null
	damageTypes: damageTypes?.map((type) => type ?? undefined),
});

type Props = {
	filters: Filters<typeof spellFilters>;
};

export const SpellFilters: React.FC<Props> = ({filters}) => (
	<FiltersPopup filters={filters} header="Фильтровать заклинания">
		<li>
			<ButtonGroup
				items={Object.keys(classes)}
				itemToHtml={recordToString(classes)}
				multiple
				label="Класс"
				{...filters.field('classes')}
			/>
		</li>
		<li>
			<ButtonGroup
				items={LEVELS}
				itemToHtml={(level) => (level === 0 ? 'Фокус' : `${level}-й`)}
				multiple
				label="Круг"
				{...filters.field('levels')}
			/>
		</li>
		<li>
			<ul className={formCss.formRow}>
				<li>
					<Checkbox label="Ритуал" {...filters.flag('ritual')} />
				</li>
				<li>
					<Checkbox label="Концентрация" {...filters.flag('concentration')} />
				</li>
			</ul>
		</li>
		<li>
			<ButtonGroup
				items={Object.values(SpellSchool)}
				itemToHtml={spellSchoolToString}
				multiple
				label="Школа"
				{...filters.field('schools')}
			/>
		</li>
		<li>
			<ButtonGroup
				items={Object.keys(components)}
				itemToHtml={recordToString(components)}
				multiple
				label="Компоненты"
				{...filters.field('components')}
			/>
		</li>
		<li>
			<ButtonGroup
				items={Object.keys(castingTimes)}
				itemToHtml={recordToString(castingTimes)}
				multiple
				label="Время сотворения"
				{...filters.field('times')}
			/>
		</li>
		<li>
			<ButtonGroup
				items={[null, ...Object.values(DamageType)]}
				itemToHtml={damageTypesToString}
				multiple
				label="Тип урона"
				{...filters.field('damageTypes')}
			/>
		</li>
		<li>
			<ButtonGroup
				items={SOURCES}
				itemToHtml={sourceBookToString}
				multiple
				label="Источник"
				{...filters.field('sources')}
			/>
		</li>
	</FiltersPopup>
);
